import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMotionValueEvent, useReducedMotion } from 'framer-motion';

/**
 * Scroll-scrubbed image sequence.
 *
 * Draws one frame of a pre-extracted sequence onto a canvas. The frame index is
 * driven by `progress`, a framer-motion MotionValue from 0 to 1 that the parent
 * derives with useScroll (the parent owns the ref of the tall wrapper the
 * canvas is pinned inside). Frames are only fetched once the canvas gets close
 * to the viewport, and if some frames are still loading the nearest loaded one
 * is drawn so the picture never goes blank.
 *
 * Props:
 *   progress   - MotionValue<number> 0..1 that scrubs the sequence
 *   frameCount - number of frames in the sequence
 *   basePath   - folder that holds the frames, e.g. "/media/process"
 *   ext        - frame extension, e.g. "webp"
 *   width/height - canvas drawing size (the CSS size is set by the parent)
 */
const ScrollSequence = ({
  progress,
  frameCount,
  basePath,
  ext = 'webp',
  width = 960,
  height = 540,
  className,
}) => {
  const canvasRef = useRef(null);
  const framesRef = useRef([]);
  const lastDrawnRef = useRef(-1);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [firstFrameReady, setFirstFrameReady] = useState(false);
  const reduceMotion = useReducedMotion();

  const frameSrc = useCallback(
    (index) => `${basePath}/${String(index + 1).padStart(3, '0')}.${ext}`,
    [basePath, ext]
  );

  // Start fetching frames only when the canvas is near the viewport.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '800px 0px' }
    );
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return undefined;
    let cancelled = false;
    const images = [];
    const markFirstFrameReady = () => {
      if (!cancelled) setFirstFrameReady(true);
    };
    for (let i = 0; i < frameCount; i += 1) {
      const img = new Image();
      img.decoding = 'async';
      if (i === 0) img.onload = markFirstFrameReady;
      img.src = frameSrc(i);
      images.push(img);
    }
    framesRef.current = images;
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, frameCount, frameSrc]);

  const drawFrame = useCallback(
    (index) => {
      const canvas = canvasRef.current;
      const frames = framesRef.current;
      if (!canvas || frames.length === 0) return;

      // Walk backwards to the nearest frame that has finished loading.
      let i = index;
      while (i >= 0 && !(frames[i].complete && frames[i].naturalWidth > 0)) i -= 1;
      if (i < 0 || i === lastDrawnRef.current) return;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(frames[i], 0, 0, canvas.width, canvas.height);
      lastDrawnRef.current = i;
    },
    []
  );

  useMotionValueEvent(progress, 'change', (value) => {
    if (reduceMotion) return;
    const index = Math.round(value * (frameCount - 1));
    window.requestAnimationFrame(() => drawFrame(Math.min(frameCount - 1, Math.max(0, index))));
  });

  // Draw the initial frame as soon as it is available. With reduced motion we
  // show the finished state instead of an unscrubbable first frame.
  useEffect(() => {
    if (!firstFrameReady) return;
    if (reduceMotion) {
      const last = framesRef.current[frameCount - 1];
      if (last) {
        const paint = () => drawFrame(frameCount - 1);
        if (last.complete) paint();
        else last.onload = paint;
      }
      return;
    }
    const current = Math.round(progress.get() * (frameCount - 1));
    drawFrame(Math.min(frameCount - 1, Math.max(0, current)));
  }, [firstFrameReady, reduceMotion, frameCount, drawFrame, progress]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      aria-hidden="true"
    />
  );
};

export default ScrollSequence;
